import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * useBankFeedData
 * --------------------------------------------------------------------------
 * Consolidates read-only / reference data fetchers used across the Bank Feed
 * Center page. Extracting these out of BankFeedCenterPage.jsx reduces its
 * surface area (state + callbacks + effects) and makes the data layer reusable
 * for any other component that needs the same context (e.g. accountant
 * workspace, dialogs that render outside the page tree).
 *
 * Returns:
 *  - connections          : bank feed connections (Mono / Plaid / etc.)
 *  - bankAccounts         : list of all bank accounts for the business
 *  - availableGlAccounts  : full chart of accounts list
 *  - aiSettings           : bank-feed AI configuration
 *  - openDocuments        : { invoices, bills } awaiting matching
 *  - monoConfig           : { public_key, enabled } for Mono Connect widget
 *  - fetch* refetchers    : callable to refresh each slice on demand
 *  - setAiSettings        : exposed because some callers (e.g. AI toggle)
 *                           mutate the cached value optimistically.
 *
 * All fetchers are guarded with a mounted-ref so unmounting mid-flight
 * cannot cause "setState on unmounted component" warnings.
 */
export function useBankFeedData() {
  const isMountedRef = useRef(true);

  const [connections, setConnections] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [availableGlAccounts, setAvailableGlAccounts] = useState([]);
  const [aiSettings, setAiSettings] = useState({ auto_post_medium_confidence: false });
  const [openDocuments, setOpenDocuments] = useState({ invoices: [], bills: [] });
  const [monoConfig, setMonoConfig] = useState({ public_key: '', enabled: false });

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchConnections = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/connections`);
      if (isMountedRef.current) setConnections(res.data.connections || []);
    } catch {
      // Silent - non-critical
    }
  }, []);

  const fetchBankAccounts = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-accounts`);
      if (isMountedRef.current) setBankAccounts(res.data || []);
    } catch {
      // Silent
    }
  }, []);

  const fetchGlAccounts = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/accounting/chart-of-accounts`);
      if (isMountedRef.current) setAvailableGlAccounts(res.data || []);
    } catch {
      // Silent
    }
  }, []);

  // iter1101 - Refetch the chart of accounts whenever ANY picker (bank feed
  // inline selector, invoice/bill line pickers, COA page) creates a new GL
  // account. Keeps every dropdown in sync without prop-drilling a callback
  // through 4-5 layers of components.
  useEffect(() => {
    const handler = () => { fetchGlAccounts(); };
    window.addEventListener('akountfly:gl-accounts-updated', handler);
    return () => window.removeEventListener('akountfly:gl-accounts-updated', handler);
  }, [fetchGlAccounts]);

  const fetchAiSettings = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/ai-settings`);
      if (isMountedRef.current) setAiSettings(res.data);
    } catch {
      // Silent
    }
  }, []);

  const fetchOpenDocuments = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/bank-feeds/open-documents`);
      if (isMountedRef.current) setOpenDocuments(res.data || { invoices: [], bills: [] });
    } catch {
      // Silent
    }
  }, []);

  const fetchMonoConfig = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      const res = await axios.get(`${API}/mono/config`);
      if (isMountedRef.current) setMonoConfig(res.data);
    } catch {
      // Silent
    }
  }, []);

  return {
    // values
    connections,
    bankAccounts,
    availableGlAccounts,
    aiSettings,
    openDocuments,
    monoConfig,
    // setters (exposed only where consumers need optimistic updates)
    setAiSettings,
    setBankAccounts,
    setConnections,
    // refetchers
    fetchConnections,
    fetchBankAccounts,
    fetchGlAccounts,
    fetchAiSettings,
    fetchOpenDocuments,
    fetchMonoConfig,
  };
}

export default useBankFeedData;
